/**
 * @file: store.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 */

import { HttpClient } from '@euler/lib/request';
import {
  OrgMemberAccountTxSearchParams,
  SimpleOrgMember,
  StoreConfiguration,
  StoreConfigurationWithHash,
  VehicleInspectionSubject,
} from '@euler/model';
import { CreateOrgMemberRequest } from '@euler/model/CreateOrgMemberRequest';
import { CustomIssueNameStat } from '@euler/model/CustomIssueNameStat';
import { FullEditionUpgradeRequestResult } from '@euler/model/FullEditionUpgradeRequest';
import { StaticMapImageParams } from '@euler/model/StaticMapImageParams';
import { CursorResult, IdType } from '@euler/model/common';
import {
  GatewayDeviceSource,
  OrgGroup,
  OrgMember,
  OrgMemberAccount,
  OrgMemberAccountTransaction,
  OrgMemberBalanceInfo,
  OrgMemberTransferRequest,
  OrgMemberTransferRequestDetail,
  OrgTeam,
  StoreErpIntegration,
  StoreErpOrderSyncLog,
  StoreGatewayDevice,
  StoreObdDevice,
} from '@euler/model/entity';
import { ErpProviderId, OrgUserRoleType } from '@euler/model/enum';
import {
  ChromeExtensionErpIntegrationSettings,
  ErpOrderSyncLogSearchParams,
  ErpOutboundOrderSyncLogRequest,
} from '@euler/model/erp-integrations';
import { getCommonRequestParametersAsSearchQuery } from '@euler/services/utils';
import { Maybe } from '@euler/types';

export class StoreService {
  constructor(private readonly api: HttpClient) {}

  async getVehicleInspectionSubjects(): Promise<VehicleInspectionSubject[]> {
    return await this.api
      .get()
      .url('/store/vehicle-inspection-subjects')
      .future();
  }

  async getCustomIssueNameStats(): Promise<CustomIssueNameStat[]> {
    return await this.api.get().url('/store/custom-issue-name-stats').future();
  }

  async getConfig(): Promise<StoreConfigurationWithHash> {
    return await this.api
      .get()
      .url('/store/config')
      .transform((storeConfig: StoreConfiguration, resp) => {
        return {
          ...storeConfig,
          __hash: resp.headers.get('x-store-config-hash') ?? '',
        } as StoreConfigurationWithHash;
      })
      .future();
  }

  /** Ask for the full edition: sales follows up. */
  async requestFullEditionUpgrade(): Promise<FullEditionUpgradeRequestResult> {
    return await this.api
      .post()
      .url('/store/full-edition-upgrade-requests')
      .future();
  }

  async hasAnyProductAuthorizationContract(): Promise<boolean> {
    return await this.api
      .get()
      .url('/store/has-any-product-authorization-contract')
      .future();
  }

  async listServiceAgents(): Promise<OrgMember[]> {
    return await this.api.get().url('/store/service-agents').future();
  }

  async listGatewayDevices(
    source: GatewayDeviceSource,
  ): Promise<StoreGatewayDevice[]> {
    return await this.api
      .get()
      .url('/store/gateway-devices', { source })
      .future();
  }

  async registerGatewayDevice(deviceInfo: {
    source: GatewayDeviceSource;
    deviceId: string;
    name?: string | null;
  }): Promise<StoreGatewayDevice> {
    return await this.api
      .post()
      .url('/store/gateway-devices')
      .data(deviceInfo)
      .future();
  }

  async updateGatewayDeviceName(
    source: GatewayDeviceSource,
    deviceId: string,
    name: string,
  ): Promise<StoreGatewayDevice> {
    return await this.api
      .put()
      .url('/store/gateway-devices/:deviceId', { deviceId })
      .data({ source, name })
      .future();
  }

  async removeGatewayDevice(
    source: GatewayDeviceSource,
    deviceId: string,
  ): Promise<void> {
    await this.api
      .delete()
      .url('/store/gateway-devices/:deviceId', { deviceId, source })
      .future();
  }

  async listObdDevices(): Promise<StoreObdDevice[]> {
    return await this.api.get().url('/store/obd-devices').future();
  }

  async registerObdDevice(deviceInfo: {
    serialNo: string;
    name: string;
    remark?: string;
  }): Promise<StoreObdDevice> {
    return await this.api
      .post()
      .url('/store/obd-devices')
      .data(deviceInfo)
      .future();
  }

  async updateObdDevice(
    id: string,
    params: {
      serialNo?: string;
      name?: string;
    },
  ): Promise<StoreObdDevice> {
    return await this.api
      .put()
      .url('/store/obd-devices/:id', { id })
      .data(params)
      .future();
  }

  async removeObdDevice(id: string): Promise<void> {
    await this.api.delete().url('/store/obd-devices/:id', { id }).future();
  }

  async getInviteLink(): Promise<string> {
    return await this.api.get().url('/store/invite-link').future();
  }

  async getInviteLinkQrcodeImageUrl(options?: {
    logo?: boolean;
    size?: number;
    expiresIn?: number;
  }): Promise<string> {
    const commonSearchQuery = await getCommonRequestParametersAsSearchQuery();
    return this.api.url('/store/invite-link/qrcode.png', {
      ...options,
      ...commonSearchQuery,
    });
  }

  async listGroups(): Promise<OrgGroup[]> {
    return await this.api.get().url('/groups').future();
  }

  async getGroup(id: number): Promise<OrgGroup> {
    return await this.api.get().url('/groups/:id', { id }).future();
  }

  async listGroupTeams(groupId: number): Promise<OrgTeam[]> {
    return await this.api
      .get()
      .url('/groups/:id/teams', { id: groupId })
      .future();
  }

  async listTeams(): Promise<OrgTeam[]> {
    return await this.api.get().url('/teams').future();
  }

  async getTeam(id: number): Promise<OrgTeam> {
    return await this.api.get().url('/teams/:id', { id }).future();
  }

  async getTeamMembers(
    teamId: number,
    options?: {
      role?: OrgUserRoleType;
      refresh?: boolean;
    },
  ): Promise<OrgMember[]> {
    return await this.api
      .get()
      .url('/teams/:id/members', { id: teamId, ...options })
      .future();
  }

  async listMembers(criterion?: {
    groupId?: number;
    teamId?: number;
    role?: OrgUserRoleType;
    refresh?: boolean;
    mobile?: string;
  }): Promise<OrgMember[]> {
    return await this.api
      .get()
      .url('/members', { ...criterion })
      .future();
  }

  async getSimpleMemberList(): Promise<SimpleOrgMember[]> {
    return await this.api.get().url('/store/simple-member-list').future();
  }

  async getMember(id: number): Promise<OrgMember | undefined> {
    return await this.api.get().url('/members/:id', { id }).future();
  }

  async createMember(request: CreateOrgMemberRequest): Promise<OrgMember> {
    return await this.api.post().url('/members/new').data(request).future();
  }

  async getMemberAccountInfo(id: number): Promise<OrgMemberAccount> {
    return await this.api.get().url('/members/:id/account', { id }).future();
  }

  async getMemberAccountTransactions(
    id: number,
    params: OrgMemberAccountTxSearchParams | undefined,
    offset: number,
    limit: number,
  ): Promise<OrgMemberAccountTransaction[]> {
    return await this.api
      .get()
      .url('/members/:id/account/transactions', {
        id,
        offset,
        limit,
        category: params?.category,
        type: params?.type ? params.type.join(',') : undefined,
      })
      .future();
  }

  async getMemberAccountTransactionDetail(
    id: number,
    txId: IdType,
  ): Promise<OrgMemberAccountTransaction> {
    return await this.api
      .get()
      .url('/members/:id/account/transactions/:txId', {
        id,
        txId,
      })
      .future();
  }

  async getMemberAccountBalances(id: number): Promise<OrgMemberBalanceInfo[]> {
    return await this.api
      .get()
      .url('/members/:id/account/balances', {
        id,
      })
      .future();
  }

  async getMemberAccountWithdrawTransferBatchRequest(
    id: number,
    batchNo: string,
  ): Promise<OrgMemberTransferRequest> {
    return await this.api
      .get()
      .url('/members/:id/account/withdraw/:batchNo', {
        id,
        batchNo,
      })
      .future();
  }

  async getMemberAccountWithdrawTransferBatchRequestDetails(
    id: number,
    batchNo: string,
  ): Promise<OrgMemberTransferRequestDetail[]> {
    return await this.api
      .get()
      .url('/members/:id/account/withdraw/:batchNo/details', {
        id,
        batchNo,
      })
      .future();
  }

  async getMemberAccountRecentWithdrawName(id: number): Promise<Maybe<string>> {
    return await this.api
      .get()
      .url('/members/:id/account/withdraw/recent-name', {
        id,
      })
      .future();
  }

  async withdrawMemberAccount(
    id: number,
    params: {
      amount: number;
      name?: string;
      ticket: string;
      simulatedExistingBatchNoForDev?: string;
    },
  ): Promise<{
    ttl: number;
    expiresIn: number;
    requestId: string;
    length: number;
  }> {
    return await this.api
      .post()
      .url('/members/:id/account/withdraw', {
        id,
      })
      .data(params)
      .future();
  }

  async confirmWithdrawMemberAccount(
    id: number,
    requestId: string,
    params: { ticket: string; verifyCode: string },
  ): Promise<{ batchNo: string }> {
    return await this.api
      .post()
      .url('/members/:id/account/withdraw/:requestId/confirm', {
        id,
        requestId,
      })
      .data(params)
      .future();
  }

  async getStaticMapImageUrl(
    params?: StaticMapImageParams,
    options?: { force?: boolean },
  ): Promise<string> {
    return await this.api
      .get()
      .url('/store/static-map-image-url', {
        ...params,
        ...options,
      })
      .future();
  }

  async getOfficialAccountQrcodeImageUrl(params?: {
    size?: number;
    margin?: number;
    format?: 'jpg' | 'png';
    logo?: boolean;
  }) {
    const commonSearchQuery = await getCommonRequestParametersAsSearchQuery();
    return this.api.url('/store/mp/qrcode', {
      ...params,
      ...commonSearchQuery,
    });
  }

  async getBrowserExtensionErpIntegration(): Promise<
    StoreErpIntegration | null | undefined
  > {
    return await this.api
      .get()
      .url('/erp-integrations/browser-extension')
      .future();
  }

  async saveBrowserExtensionErpIntegrationSettings(
    erpProviderId: ErpProviderId,
    settings: ChromeExtensionErpIntegrationSettings,
    requestedBy: number,
    requestedByName: string,
  ): Promise<StoreErpIntegration | null | undefined> {
    return await this.api
      .post()
      .url('/erp-integrations/browser-extension')
      .data({ erpProviderId, settings, requestedBy, requestedByName })
      .future();
  }

  async removeBrowserExtensionErpIntegration(
    requestedBy: number,
    requestedByName: string,
  ): Promise<StoreErpIntegration | null | undefined> {
    return await this.api
      .delete()
      .url('/erp-integrations/browser-extension')
      .data({ requestedBy, requestedByName })
      .future();
  }

  async activateBrowserExtensionErpIntegration(
    requestedBy: number,
    requestedByName: string,
  ): Promise<StoreErpIntegration | null | undefined> {
    return await this.api
      .patch()
      .url('/erp-integrations/browser-extension/activate')
      .data({ requestedBy, requestedByName })
      .future();
  }

  async deactivateBrowserExtensionErpIntegration(
    requestedBy: number,
    requestedByName: string,
  ): Promise<StoreErpIntegration | null | undefined> {
    return await this.api
      .patch()
      .url('/erp-integrations/browser-extension/deactivate')
      .data({ requestedBy, requestedByName })
      .future();
  }

  async searchErpOrderSyncLogs(
    params: ErpOrderSyncLogSearchParams,
    sortDir: 'asc' | 'desc',
    nextKey: string | undefined,
    limit: number,
  ): Promise<CursorResult<StoreErpOrderSyncLog, string>> {
    return await this.api
      .post()
      .url('/erp-integrations/order-sync-logs/search')
      .data({ params, sortDir, nextKey, limit })
      .future();
  }

  async logErpOutboundOrderSyncResult(request: ErpOutboundOrderSyncLogRequest) {
    await this.api
      .post()
      .url('/erp-integrations/browser-extension/outbound-order-sync-logs')
      .data({ request })
      .future();
  }
}
